import { SchmancyElement } from '@schmancy/mixins/index'
import { css, html, PropertyValues } from 'lit'
import { customElement, property, state } from 'lit/decorators.js'
import { createRef, ref } from 'lit/directives/ref.js'

const DEFAULT_BASE_CSS = `html,body{margin:0;padding:0;overflow:hidden;background:#fff;color:#1a1a1a;font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,'Helvetica Neue',Arial,sans-serif;font-size:14px;line-height:1.6;word-wrap:break-word;overflow-wrap:break-word}
body{padding:16px}
p{margin:0 0 1em}p:last-child{margin-bottom:0}
ul,ol{margin:0 0 1em;padding-left:1.5em}li{margin-bottom:.25em}
h1,h2,h3,h4{margin:0 0 .5em;line-height:1.3}h1{font-size:1.5em}h2{font-size:1.25em}h3{font-size:1.1em}h4{font-size:1em}
hr{border:none;border-top:1px solid #dadce0;margin:1em 0}
img{max-width:100%;height:auto}
table{border-collapse:collapse;max-width:100%}td,th{padding:4px 8px;border:1px solid #dadce0}
blockquote{margin:0 0 1em;padding:.5em 0 .5em 1em;border-left:3px solid #dadce0;color:#5f6368}
pre{background:#f5f5f5;padding:.75em;border-radius:var(--schmancy-radius-xs,0.4375rem);overflow-x:auto;font-size:.9em}
code{background:#f5f5f5;padding:.1em .3em;border-radius:var(--schmancy-radius-xs,0.4375rem);font-size:.9em}`

@customElement('schmancy-iframe')
export default class SchmancyIframe extends SchmancyElement {
	static styles = [
		css`
			:host {
				display: block;
			}
			iframe {
				border: 0;
				width: 100%;
			}
		`,
	]
	@property({ type: String }) html = ''

	@property({ type: String }) css = ''

	@property({ type: String }) baseCss = DEFAULT_BASE_CSS

	@property({ type: String }) sandbox = 'allow-same-origin allow-popups'

	@property({ type: Number }) minHeight = 60

	@state() private height = 60
	private srcdoc = ''
	private iframeRef = createRef<HTMLIFrameElement>()

	protected willUpdate(changed: PropertyValues) {
		if (changed.has('html') || changed.has('css') || changed.has('baseCss')) {
			this.srcdoc = this.html ? this.buildSrcdoc() : ''
			this.height = this.minHeight
		}
	}

	protected updated(changed: PropertyValues) {
		if (changed.has('sandbox')) {
			this.iframeRef.value?.setAttribute('sandbox', this.sandbox)
		}
	}

	private buildSrcdoc(): string {
		const styles = this.css ? `${this.baseCss}\n${this.css}` : this.baseCss
		// A generated document is its own custom-property scope — the host page's
		// ladder never inherits across the frame boundary, so a bare var() inside
		// would sit on its fallback forever. The rung the injected CSS uses is
		// copied into this document's own :root at write time; when the service is
		// at its anchor it declares nothing on documentElement and the read is
		// empty, which is correct — the var()'s own fallback is that same anchor.
		const xs = getComputedStyle(this).getPropertyValue('--schmancy-radius-xs').trim()
		const ladder = xs ? `:root{--schmancy-radius-xs:${xs}}` : ''
		return `<!DOCTYPE html><html><head>
<meta charset="utf-8"><meta name="viewport" content="width=device-width,initial-scale=1">
<base target="_blank">
<style>${ladder}${styles}</style></head><body>${this.html}</body></html>`
	}

	private onLoad(e: Event) {
		const iframe = e.target as HTMLIFrameElement
		try {
			const doc = iframe.contentDocument
			if (!doc) return
			this.height = Math.max(doc.documentElement.scrollHeight, this.minHeight)
		} catch {
			this.height = Math.max(200, this.minHeight)
		}
	}

	protected render() {
		if (!this.html) return html``
		return html`<iframe
			${ref(this.iframeRef)}
			.srcdoc=${this.srcdoc}
			style="height:${this.height}px;min-height:${this.minHeight}px;overflow:hidden"
			@load=${this.onLoad}
		></iframe>`
	}
}

declare global {
	interface HTMLElementTagNameMap {
		'schmancy-iframe': SchmancyIframe
	}
}
